import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { MrpIncomingPeekHoverHintBody } from "@/components/mrp/MrpIncomingPeekHoverHintBody";
import { MrpOnHandPeekHoverHintBody } from "@/components/mrp/MrpOnHandPeekHoverHintBody";
import { MrpOverstockValueHoverHintBody } from "@/components/mrp/MrpOverstockValueHoverHintBody";
import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintContent, HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import {
  buildMrpIncomingPartsPath,
  compareMrpForecastStackOrder,
  firstOfThisMonth,
  formatNumber,
  mrpForecastCacheKey,
  mrpOverstockCacheKey,
  type MrpOverstockResult,
  type MrpOverstockRow,
  type MrpPlanningLocationApiRow,
} from "@/lib/mrp";
import { useHubCacheStream } from "@/lib/useHubCacheStream";

type SortKey =
  | "cpn"
  | "description"
  | "on_hand"
  | "on_order"
  | "avg_monthly_demand"
  | "months_of_supply"
  | "months_to_reorder"
  | "overstock_qty"
  | "unit_price"
  | "overstock_value";

const OVERSTOCK_PAGE_HELP =
  "Only SKUs with planning mode MRP on public.sku (not reorder point, build plan, make-to-order, or unset). Rows with a shortage in the current forecast month are omitted. On-hand is physical stock across forecast sites. On order is open incoming purchase orders and work orders from the latest inventory snapshot. Months of supply divides on-hand plus on-order by average monthly build-plan demand. Overstock quantity is projected surplus after the forecast horizon when demand exists, or full supply position when the build plan shows no demand. Overstock dollar value is on-hand plus on-order at the latest purchase order unit price in USD (CNY purchase orders are converted using the daily CNY-per-USD rate on the PO date), falling back to Megaventory default purchase price. Hover the Overstock $ cell for a quantity and value breakdown. Reorder-point overstock would need a separate report based on reserve targets.";

function formatMonthsOfSupply(row: MrpOverstockRow): string {
  if (row.has_zero_forecast_demand) return "No demand";
  if (row.months_of_supply == null) return "—";
  if (row.months_of_supply >= 999) return "999+";
  return row.months_of_supply.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatMonthsToReorder(value: number | null, horizonMonths: number): string {
  if (value == null) return `>${horizonMonths} mo`;
  return String(value);
}

function formatCurrency(value: number | null): string {
  if (value == null) return "—";
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function formatUnitPrice(value: number | null): string {
  if (value == null) return "—";
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function rowMatchesSearch(row: MrpOverstockRow, needle: string): boolean {
  if (needle === "") return true;
  const haystack = [row.cpn, row.display_name ?? ""].join(" ").toLowerCase();
  return haystack.includes(needle);
}

function compareRows(
  left: MrpOverstockRow,
  right: MrpOverstockRow,
  key: SortKey,
  dir: "asc" | "desc",
): number {
  const sign = dir === "asc" ? 1 : -1;
  if (key === "cpn") {
    return sign * left.cpn.localeCompare(right.cpn, undefined, { sensitivity: "base" });
  }
  if (key === "description") {
    return (
      sign *
      (left.display_name ?? "").localeCompare(right.display_name ?? "", undefined, {
        sensitivity: "base",
      })
    );
  }
  if (key === "avg_monthly_demand") {
    const leftVal = left.has_zero_forecast_demand ? -1 : left.avg_monthly_demand;
    const rightVal = right.has_zero_forecast_demand ? -1 : right.avg_monthly_demand;
    return sign * (leftVal - rightVal);
  }
  if (key === "unit_price") {
    return sign * ((left.unit_price ?? -1) - (right.unit_price ?? -1));
  }
  if (key === "months_of_supply") {
    const leftVal = left.has_zero_forecast_demand
      ? Number.POSITIVE_INFINITY
      : (left.months_of_supply ?? -1);
    const rightVal = right.has_zero_forecast_demand
      ? Number.POSITIVE_INFINITY
      : (right.months_of_supply ?? -1);
    return sign * (leftVal - rightVal);
  }
  if (key === "months_to_reorder") {
    const leftVal = left.months_to_reorder ?? Number.POSITIVE_INFINITY;
    const rightVal = right.months_to_reorder ?? Number.POSITIVE_INFINITY;
    return sign * (leftVal - rightVal);
  }
  const leftNum = left[key] ?? -1;
  const rightNum = right[key] ?? -1;
  return sign * (Number(leftNum) - Number(rightNum));
}

export function Overstock(): JSX.Element {
  const queryClient = useQueryClient();
  const [startMonth] = useState(firstOfThisMonth());
  const [horizon] = useState(12);
  const [search, setSearch] = useState("");
  const [overstockOnly, setOverstockOnly] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("overstock_value");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const locationsQuery = useQuery<{ locations: MrpPlanningLocationApiRow[] }, ApiError>({
    queryKey: ["mrp", "locations"],
    queryFn: () => apiFetch<{ locations: MrpPlanningLocationApiRow[] }>("/api/mrp/locations"),
    staleTime: 5 * 60_000,
  });

  const forecastLocationCodes = useMemo(() => {
    const rows = (locationsQuery.data?.locations ?? []).filter((row) => row.include_in_forecast);
    const sorted = [...rows];
    sorted.sort((left, right) => compareMrpForecastStackOrder(left.code, right.code));
    return sorted.map((row) => row.code);
  }, [locationsQuery.data?.locations]);

  const overstockQueryKey = ["mrp", "overstock", { startMonth, horizon, candidatesOnly: overstockOnly }] as const;
  const overstockHubCacheChannel = HUB_CACHE_CHANNEL.mrpOverstock(
    mrpOverstockCacheKey({ startMonth, horizon, candidatesOnly: overstockOnly }),
  );
  const forecastHubCacheChannel =
    forecastLocationCodes.length > 0
      ? HUB_CACHE_CHANNEL.mrpForecast(
          mrpForecastCacheKey({ startMonth, horizon, locations: forecastLocationCodes }),
        )
      : null;

  const overstockQuery = useQuery<MrpOverstockResult, ApiError>({
    queryKey: overstockQueryKey,
    queryFn: () =>
      apiFetch<MrpOverstockResult>("/api/mrp/overstock", {
        query: {
          start_month: startMonth,
          horizon,
          candidates_only: overstockOnly ? "1" : "0",
          background: "1",
        },
      }),
    enabled: forecastLocationCodes.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  const overstockCpnsQueryParam = useMemo(() => {
    const cpns = (overstockQuery.data?.rows ?? []).map((row) => row.cpn);
    return [...new Set(cpns.map((cpn) => cpn.trim()).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right))
      .join(",");
  }, [overstockQuery.data?.rows]);

  const overstockSkuHintsQuery = useQuery<
    { sku_hover_hints: Record<string, import("@/lib/mrp").MrpSkuHoverHintFields> },
    ApiError
  >({
    queryKey: ["mrp", "sku-hover-hints", overstockCpnsQueryParam] as const,
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, import("@/lib/mrp").MrpSkuHoverHintFields> }>(
        "/api/mrp/sku-hover-hints",
        { query: { cpns: overstockCpnsQueryParam } },
      ),
    enabled: overstockCpnsQueryParam.length > 0 && overstockQuery.isSuccess,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  useHubCacheStream({
    channels: forecastHubCacheChannel != null ? [overstockHubCacheChannel, forecastHubCacheChannel] : [overstockHubCacheChannel],
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: overstockQueryKey });
      }
    },
  });

  const skuHoverHints = overstockSkuHintsQuery.data?.sku_hover_hints ?? {};
  const horizonMonths = overstockQuery.data?.horizon_months ?? horizon;

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = (overstockQuery.data?.rows ?? []).filter((row) => rowMatchesSearch(row, needle));
    return [...list].sort((left, right) => compareRows(left, right, sortKey, sortDir));
  }, [overstockQuery.data?.rows, search, sortDir, sortKey]);

  const totals = useMemo(() => {
    let overstockValue = 0;
    let withValue = 0;
    for (const row of filteredRows) {
      if (row.overstock_value != null) {
        overstockValue += row.overstock_value;
        withValue += 1;
      }
    }
    return { count: filteredRows.length, overstockValue, withValue };
  }, [filteredRows]);

  function handleSort(nextKey: SortKey): void {
    if (sortKey === nextKey) {
      setSortDir((previous) => (previous === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "cpn" ? "asc" : "desc");
  }

  const filtersActive = search.trim() !== "" || !overstockOnly;

  const overstockColumns = useMemo((): HubDataTableColumn<MrpOverstockRow, SortKey>[] => {
    return [
      {
        id: "cpn",
        header: "SKU",
        sortKey: "cpn",
        overflow: "nowrap",
        sortValue: (row) => row.cpn,
        cell: (row) => {
          const hint = skuHoverHints[row.cpn];
          return (
            <span className="font-mono text-xs">
              <HoverHintContent hoverHint={<MrpSkuCatalogHoverHintBody sku={row.cpn} hint={hint} />} block>
                <MrpSkuDetailLink sku={row.cpn} tab="inventory" />
              </HoverHintContent>
            </span>
          );
        },
      },
      {
        id: "description",
        header: "Description",
        sortKey: "description",
        sortValue: (row) => row.display_name ?? "",
        cell: (row) => {
          const hint = skuHoverHints[row.cpn];
          const description = row.display_name?.trim() || "—";
          return (
            <HoverHintContent
              hoverHint={<MrpSkuCatalogHoverHintBody sku={row.cpn} hint={hint} />}
              block
              cursor="default"
            >
              <span className="line-clamp-2 text-xs text-slate-700">{description}</span>
            </HoverHintContent>
          );
        },
      },
      {
        id: "on_hand",
        header: "On hand",
        sortKey: "on_hand",
        align: "right",
        sortValue: (row) => row.on_hand,
        cell: (row) => (
          <HoverHintTrigger
            align="start"
            trigger={
              <span className="cursor-help tabular-nums underline decoration-dotted decoration-slate-400 underline-offset-2">
                {formatNumber(row.on_hand)}
              </span>
            }
          >
            <MrpOnHandPeekHoverHintBody cpn={row.cpn} forecastLocationCodes={forecastLocationCodes} />
          </HoverHintTrigger>
        ),
      },
      {
        id: "on_order",
        header: "On order",
        sortKey: "on_order",
        align: "right",
        sortValue: (row) => row.on_order,
        cell: (row) => (
          <HoverHintTrigger
            align="start"
            closeOnTriggerActivate={row.on_order > 0}
            trigger={
              row.on_order > 0 ? (
                <Link
                  to={buildMrpIncomingPartsPath({ cpn: row.cpn })}
                  className="text-sky-800 underline decoration-sky-300 underline-offset-2 hover:text-sky-950"
                >
                  {formatNumber(row.on_order)}
                </Link>
              ) : (
                <span className="tabular-nums">{formatNumber(row.on_order)}</span>
              )
            }
          >
            <MrpIncomingPeekHoverHintBody cpn={row.cpn} />
          </HoverHintTrigger>
        ),
      },
      {
        id: "avg_monthly_demand",
        header: "Avg mo. demand",
        sortKey: "avg_monthly_demand",
        align: "right",
        sortValue: (row) => (row.has_zero_forecast_demand ? -1 : row.avg_monthly_demand),
        cell: (row) =>
          row.has_zero_forecast_demand
            ? "—"
            : row.avg_monthly_demand.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      },
      {
        id: "months_of_supply",
        header: "Months supply",
        sortKey: "months_of_supply",
        align: "right",
        sortValue: (row) =>
          row.has_zero_forecast_demand
            ? Number.POSITIVE_INFINITY
            : (row.months_of_supply ?? -1),
        cell: (row) => formatMonthsOfSupply(row),
      },
      {
        id: "months_to_reorder",
        header: "Reorder in mo.",
        sortKey: "months_to_reorder",
        align: "right",
        sortValue: (row) => row.months_to_reorder ?? Number.POSITIVE_INFINITY,
        cell: (row) => formatMonthsToReorder(row.months_to_reorder, horizonMonths),
      },
      {
        id: "overstock_qty",
        header: "Overstock qty",
        sortKey: "overstock_qty",
        align: "right",
        sortValue: (row) => row.overstock_qty,
        cell: (row) => formatNumber(row.overstock_qty),
      },
      {
        id: "unit_price",
        header: "Unit price",
        sortKey: "unit_price",
        align: "right",
        sortValue: (row) => row.unit_price ?? -1,
        cell: (row) => formatUnitPrice(row.unit_price),
      },
      {
        id: "overstock_value",
        header: "Overstock $",
        sortKey: "overstock_value",
        align: "right",
        sortValue: (row) => row.overstock_value ?? -1,
        cell: (row) =>
          row.overstock_value != null ? (
            <HoverHintContent hoverHint={<MrpOverstockValueHoverHintBody row={row} />} align="center">
              <span className="cursor-help font-medium underline decoration-dotted decoration-slate-400 underline-offset-2">
                {formatCurrency(row.overstock_value)}
              </span>
            </HoverHintContent>
          ) : (
            formatCurrency(row.overstock_value)
          ),
      },
    ];
  }, [forecastLocationCodes, horizonMonths, skuHoverHints]);

  return (
    <div className="space-y-6">
      <header>
        <PageTitleWithHelp hoverHint={OVERSTOCK_PAGE_HELP}>Overstock</PageTitleWithHelp>
        {overstockQuery.data ? (
          <p className="mt-1 text-sm text-slate-600">
            Forecast window {overstockQuery.data.start_month.slice(0, 7)} through{" "}
            {overstockQuery.data.end_month.slice(0, 7)} ({overstockQuery.data.horizon_months} months).{" "}
            <Link to="/mrp/forecast" className="text-sky-700 underline hover:text-sky-900">
              Open forecast
            </Link>
          </p>
        ) : null}
      </header>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-col gap-3 pt-0 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[14rem] flex-1">
            <Label htmlFor="overstock-search">Search SKU or description</Label>
            <Input
              id="overstock-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter rows…"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 pb-1">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">View</span>
            <Button
              type="button"
              size="sm"
              variant={overstockOnly ? "primary" : "secondary"}
              onClick={() => setOverstockOnly((previous) => !previous)}
            >
              Overstock candidates only
            </Button>
          </div>
        </CardBody>
      </Card>

      {overstockQuery.data || overstockQuery.isLoading ? (
        <HubDataTable
          title={
            overstockQuery.isLoading && !overstockQuery.data
              ? "Loading overstock report…"
              : `${totals.count} SKU${totals.count === 1 ? "" : "s"}`
          }
          headerExtra={
            overstockQuery.data ? (
              <p className="text-sm text-slate-600">
                Overstock value (filtered): {formatCurrency(totals.overstockValue)}
                {totals.withValue < totals.count ? (
                  <span className="text-slate-500">
                    {" "}
                    · {totals.count - totals.withValue} without unit price
                  </span>
                ) : null}
              </p>
            ) : null
          }
          columns={overstockColumns}
          rows={filteredRows}
          rowKey={(row) => row.cpn}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          loading={overstockQuery.isLoading && !overstockQuery.data}
          loadingMessage="Loading overstock report…"
          error={overstockQuery.isError ? overstockQuery.error.message : null}
          empty="No overstock rows."
          isFiltered={filtersActive}
          filteredEmpty="No rows match the current filters."
          tableClassName="min-w-full border-collapse text-sm"
        />
      ) : overstockQuery.isError ? (
        <p className="text-sm text-red-700">{overstockQuery.error.message}</p>
      ) : null}
    </div>
  );
}
